/*
Envia la imagen del vehículo al servidor.
Autor: Benjamín León
Fecha. 2024-09-05
Modificado: 2025-01-15 - Added client-side HEIC conversion
*/
import { useState } from 'react'
import imageCompression from 'browser-image-compression'

import { getToken } from '../utils/utils'
import { convertHeicToJpeg, hasHeicFilesEnhanced } from '../utils/heicConverter'

import { makeUrlPictureCrea } from './connectionData'

export interface UserOrganization {
  user_id: string
  organization_id: string
  vehicle_id: string
  registry: string
  attachment: string
}

const useFileUpload = (
  userOrg: UserOrganization,
  onSuccess: (newImageUrl?: string[]) => void // <-- Callback function for success
) => {
  const url = makeUrlPictureCrea()
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [isConverting, setIsConverting] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [conversionProgress, setConversionProgress] = useState<{
    current: number
    total: number
    filename: string
  } | null>(null)

  const handleFileChangeEx = async (event: React.ChangeEvent<HTMLInputElement>, description?: string) => {
    const selectedFiles = event.target.files ?? null

    if (selectedFiles) {
      await uploadFile(selectedFiles, description)
    }
  }

  const uploadFile = async (filesToUpload: FileList, description?: string) => {
    const filesArray = Array.from(filesToUpload)

    try {
      setIsUploading(true)
      setError(null)

      // Check if any files need conversion (uses enhanced detection with magic bytes)
      const needsConversion = await hasHeicFilesEnhanced(filesArray)

      if (needsConversion) {
        setIsConverting(true)
        console.log('🔄 HEIC files detected (including renamed files), converting before upload...')
      }

      // Convert HEIC files to JPEG in browser and apply compression to fix EXIF orientation
      const convertedFiles: File[] = []

      for (let i = 0; i < filesArray.length; i++) {
        const file = filesArray[i]

        if (needsConversion) {
          setConversionProgress({
            current: i + 1,
            total: filesArray.length,
            filename: file.name,
          })
        }

        // Convert if HEIC, otherwise use original
        const result = await convertHeicToJpeg(file, 0.9)
        let finalFile = result.file

        if (result.wasConverted) {
          console.log(`✅ Converted ${result.originalName} → ${result.file.name}`)
        }

        // Apply browser-image-compression to strip EXIF and fix orientation
        try {
          // Skip document attachments if they are not images
          if (finalFile.type.startsWith('image/')) {
            const options = {
              maxSizeMB: 3, // Prevent excessively large uploads while keeping good quality
              maxWidthOrHeight: 2500,
              useWebWorker: true,
            }
            console.log(`🔄 Applying EXIF orientation and compression to ${finalFile.name}...`)
            try {
              const compressedBlob = await imageCompression(finalFile, options)
              finalFile = new File([compressedBlob], finalFile.name, {
                type: compressedBlob.type,
                lastModified: Date.now(),
              })
              console.log(`✅ EXIF orientation applied to ${finalFile.name}`)
            } catch (workerError) {
              console.warn(`⚠️ Compression with WebWorker failed, retrying without WebWorker...`, workerError)
              options.useWebWorker = false
              const compressedBlob = await imageCompression(finalFile, options)
              finalFile = new File([compressedBlob], finalFile.name, {
                type: compressedBlob.type,
                lastModified: Date.now(),
              })
              console.log(`✅ EXIF orientation applied to ${finalFile.name} (without WebWorker)`)
            }
          }
        } catch (compError) {
          console.error(`❌ Error fixing image orientation for ${finalFile.name}:`, compError)
          // If compression fails, fall back to the uncompressed/original file
        }

        convertedFiles.push(finalFile)
      }

      setIsConverting(false)
      setConversionProgress(null)

      // Now upload the converted files
      const formData = new FormData()

      for (const file of convertedFiles) {
        formData.append('image', file)
      }

      // Append otherData as key-value pairs to the FormData
      Object.keys(userOrg).forEach((key) => {
        formData.append(key, userOrg[key as keyof UserOrganization])
      })

      if (description) {
        formData.append('description', description)
      }

      const token = getToken()

      const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      }

      const response = await fetch(url, {
        method: 'POST',
        body: formData,
        headers: headers,
      })

      if (!response.ok) {
        throw new Error(`Error uploading image: ${response.statusText}`)
      }

      const result = await response.json()
      onSuccess(result.pictureIdArr)

      console.log('✅ Upload successful!')
    } catch (uploadError) {
      const errorMessage = uploadError instanceof Error ? uploadError.message : 'Error uploading image'
      setError(errorMessage)
      console.error('❌ Upload error:', uploadError)
    } finally {
      setIsUploading(false)
      setIsConverting(false)
      setConversionProgress(null)
    }
  }

  return {
    isUploading,
    isConverting,
    conversionProgress,
    error,
    handleFileChangeEx,
  }
}

export default useFileUpload
